import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import AuthLayout from "../../shared/ui/AuthLayout";
import AuthSectionHeader from "../../shared/ui/AuthSectionHeader";
import Button from "../../shared/ui/Button";
import Input from "../../shared/ui/Input";
import { useTranslation } from "../../i18n";
import api from "../../utils/api";
import { getGuestReturnPath, isAuthenticated } from "../../lib/authRoutes";

const InlineSpinner = () => (
  <svg className="h-4 w-4 animate-spin text-white" viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.35)" strokeWidth="3" />
    <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

const ResetPasswordForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { uid, token } = useParams();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Auth-aware navigation: a logged-in user (Settings → change password)
  // returns to their dashboard, a logged-out user to the login screen.
  const returnPath = getGuestReturnPath();
  const loggedIn = isAuthenticated();
  const returnLabel = loggedIn
    ? t("common.backToDashboard")
    : t("auth.goToLogin");
  const backLabel = loggedIn
    ? t("common.backToDashboard")
    : t("auth.backToLogin");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (password.length < 8) {
      setError(t("auth.resetTooShort"));
      return;
    }
    if (password !== confirm) {
      setError(t("auth.resetMismatch"));
      return;
    }

    setError("");
    setIsSubmitting(true);
    try {
      await api.post("/api/users/password-reset/confirm/", {
        uid,
        token,
        password,
      });
      setDone(true);
    } catch (err) {
      if (err?.data?.code === "invalid_token") {
        setError(t("auth.resetInvalidToken"));
      } else if (err?.data?.detail) {
        setError(err.data.detail);
      } else {
        setError(t("auth.resetGenericError"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (done) {
    return (
      <AuthLayout consentText={t("auth.consentPrefix")}>
        <AuthSectionHeader
          title={t("auth.resetSuccessTitle")}
          description={t("auth.resetSuccessBody")}
        />
        <div className="mt-6">
          <Button type="button" onClick={() => navigate(returnPath)}>
            {returnLabel}
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout consentText={t("auth.consentPrefix")}>
      <form onSubmit={handleSubmit}>
        <AuthSectionHeader
          title={t("auth.resetTitle")}
          description={t("auth.resetSubtitle")}
        />

        <div className="space-y-6">
          <Input
            type={showPassword ? "text" : "password"}
            name="password"
            label={t("auth.resetNewPassword")}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setError("");
            }}
            placeholder="••••••••••••"
            rightElement={
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="text-[#4b5563] transition hover:text-black"
                aria-label={showPassword ? "Passwort ausblenden" : "Passwort anzeigen"}
              >
                {showPassword ? "🙈" : "👁"}
              </button>
            }
          />

          <Input
            type={showPassword ? "text" : "password"}
            name="confirm"
            label={t("auth.resetConfirmPassword")}
            value={confirm}
            onChange={(e) => {
              setConfirm(e.target.value);
              setError("");
            }}
            placeholder="••••••••••••"
          />

          <Button type="submit" showArrow={!isSubmitting} className="mt-2" disabled={isSubmitting}>
            {isSubmitting ? (
              <span className="inline-flex items-center gap-2">
                <InlineSpinner />
                <span>{t("auth.resetSaving")}</span>
              </span>
            ) : (
              t("auth.resetSubmit")
            )}
          </Button>

          {error && (
            <p className="text-[13px] text-red-600" role="alert">
              {error}
            </p>
          )}
        </div>

        <div className="my-8 h-px w-full bg-[#ececec]" />

        <div className="pt-1 text-center">
          <Link
            to={returnPath}
            className="text-[14px] font-medium text-[#4b5563] transition hover:text-black"
          >
            {backLabel}
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
};

export default ResetPasswordForm;
